import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, History } from "lucide-react";
import { importApi } from "../../lib/api";
import { getApiError } from "../../lib/utils";

/** Legacy suspension/reactivation backfill — inserts backdated audit rows
 *  from a CSV/XLSX. Does not broadcast to Telegram. */
export const LegacyBackfillTab = () => {
  const legacyRef = useRef(null);
  const [legacyFile, setLegacyFile] = useState(null);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [legacyReport, setLegacyReport] = useState(null);

  const submit = async () => {
    if (!legacyFile) return;
    setLegacyLoading(true);
    setLegacyReport(null);
    try {
      const r = await importApi.uploadLegacy(legacyFile);
      setLegacyReport(r);
      toast.success(`Inserted ${r.inserted} legacy audit rows`);
      setLegacyFile(null);
    } catch (e) {
      toast.error(getApiError(e, "Legacy import failed"));
    } finally { setLegacyLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
        <div className="font-bold uppercase tracking-wider mb-1">Legacy backfill</div>
        Import historical suspensions/reactivations as backdated audit entries.
        Expected columns (CSV or XLSX): <code>facility_id</code> or <code>facility_name</code>,
        <code>new_status</code> (ACTIVE|SUSPENDED), <code>reason</code>, <code>timestamp</code> (YYYY-MM-DD),
        optional <code>old_status</code>, <code>actor_name</code>, and <code>apply_current_status</code> (true to also flip today's status).
        This <b>does not broadcast</b> to Telegram.
      </div>
      <div
        onClick={() => legacyRef.current?.click()}
        className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-10 text-center hover:bg-slate-100"
      >
        <input
          ref={legacyRef}
          type="file"
          accept=".xlsx,.xlsm,.csv"
          className="hidden"
          onChange={(e) => setLegacyFile(e.target.files?.[0] || null)}
        />
        <History size={22} className="text-nmi-700" />
        <div className="mt-2 font-heading text-lg font-bold text-slate-900">
          {legacyFile ? legacyFile.name : "Drop legacy suspension file"}
        </div>
        <div className="mt-1 text-xs text-slate-500">.csv or .xlsx accepted</div>
      </div>
      <div className="flex justify-end gap-2">
        {legacyFile && <button onClick={() => setLegacyFile(null)} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">Clear</button>}
        <button
          onClick={submit}
          disabled={!legacyFile || legacyLoading}
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
        >
          {legacyLoading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} strokeWidth={2.5} />}
          Import legacy suspensions
        </button>
      </div>
      {legacyReport && (
        <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
          <div className="font-bold">Inserted {legacyReport.inserted} audit rows.</div>
          <div>Applied to current status: {legacyReport.applied_current_status}. Total rows in file: {legacyReport.total_rows}.</div>
          {legacyReport.errors?.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer font-semibold">{legacyReport.errors.length} row errors</summary>
              <ul className="mt-1 list-disc pl-4">
                {legacyReport.errors.map((e, i) => <li key={`legerr-${i}-${e.slice(0, 20)}`}>{e}</li>)}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};
